import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { DELETE_BROADCAST_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import { GET_BROADCAST_ACQUISITIONS } from '@optools/services/operator-gateway/btc/queries';
import {
  PipelineBroadcastOverride,
  Region,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface DeleteBroadcastOverrideDialogProps {
  feedTypeCode: string;
  gamePk: number;
  override: PipelineBroadcastOverride;
}

export function DeleteBroadcastOverrideDialog({
  feedTypeCode,
  gamePk,
  override,
}: DeleteBroadcastOverrideDialogProps) {
  const [open, setOpen] = useState(false);

  const [deleteBroadcastOverride, { loading }] = useMutation(DELETE_BROADCAST_OVERRIDE, {
    onCompleted: ({ deleteBroadcastOverride }) => {
      const title = 'Delete Broadcast Override';

      if (!deleteBroadcastOverride) {
        toast.error(title, {
          description: `Failed to delete Broadcast Override ID: ${override.id}`,
          duration: Infinity,
        });
        return;
      }

      setOpen(false);
      toast.success(title, { description: `Deleted Broadcast Override ID: ${override.id}` });
    },
    refetchQueries: [
      {
        query: GET_BROADCAST_ACQUISITIONS,
        variables: { feedTypeCode, gamePk },
      },
    ],
  });

  const {
    callSign,
    fronthaulDecoderInputLabel: inputLabel,
    id,
    multicastAddress,
    region,
  } = override;

  const handleDeleteOverride = () => {
    deleteBroadcastOverride({ variables: { id, region: region as Region } });
  };

  return (
    <AlertDialog open={open} onOpenChange={(newOpen) => !loading && setOpen(newOpen)}>
      <AlertDialogTrigger asChild>
        <Button aria-label="Delete Broadcast Override" size="icon-xs">
          <Trash />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogDescription className="sr-only">
          Delete an existing broadcast override
        </AlertDialogDescription>
        <AlertDialogTitle>Delete Broadcast Override</AlertDialogTitle>
        <div className="flex flex-col justify-center gap-1 text-sm">
          <dl className="flex gap-1">
            <dt className="opacity-70">Call Sign:</dt>
            <dd className="font-semibold">{callSign}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="opacity-70">Region:</dt>
            <dd className="font-semibold">{region}</dd>
          </dl>
          <dl className="flex gap-1" data-testid="decoder-input-label">
            <dt className="opacity-70">Input Label:</dt>
            <dd className="font-semibold">{inputLabel ? inputLabel : 'None'}</dd>
          </dl>
          <dl className="flex gap-1" data-testid="multicast-address">
            <dt className="opacity-70">Multicast Address:</dt>
            <dd className="font-semibold">{multicastAddress ? multicastAddress : 'None'}</dd>
          </dl>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            disabled={loading}
            loading={loading}
            variant="destructive"
            onClick={handleDeleteOverride}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
